import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, X } from "lucide-react";

import axios from "../../../services/axiosInstance";
import { fetchMasterWarehouses } from "../raking/warehouseSlice";
import { Panel, PanelHeader, formatNumber } from "../../components/DashboardKpiCards";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

/**
 * Lokasi palet — tabel yang sama dengan bagian "Lokasi palet" di dashboard.
 * Data: GET /report-pallet-capacity -> bin_locations
 */
export default function LocationPage() {
    const dispatch = useDispatch();
    const warehouses = useSelector((state) => state.masterWarehouses?.data) || [];

    const [warehouseId, setWarehouseId] = useState("");
    const [search, setSearch] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    const requestId = useRef(0);

    useEffect(() => {
        if (!warehouses.length) dispatch(fetchMasterWarehouses());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // ambil data setiap warehouse berubah
    useEffect(() => {
        const id = ++requestId.current;
        setLoading(true);
        setError(null);

        axios
            .get("/report-pallet-capacity", {
                params: { warehouse_id: warehouseId || undefined }
            })
            .then((res) => {
                if (id !== requestId.current) return;
                setRows(Array.isArray(res.data?.bin_locations) ? res.data.bin_locations : []);
                setPage(1);
            })
            .catch((err) => {
                if (id !== requestId.current) return;
                setRows([]);
                setError(
                    err.response?.data?.message ||
                    "Data lokasi palet tidak bisa diambil. Periksa koneksi ke server lalu coba lagi."
                );
            })
            .finally(() => {
                if (id === requestId.current) setLoading(false);
            });
    }, [warehouseId]);

    const isAllWarehouse = !warehouseId;

    // cari di kode palet, rak, SKU, nama item, batch
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            [r.kode_palet, r.racks_name, r.rack_code, r.sku, r.description, r.batch_number, r.level_code]
                .some((v) => String(v ?? "").toLowerCase().includes(q))
        );
    }, [rows, search]);

    const totalQty = useMemo(
        () => filtered.reduce((a, r) => a + Number(r.qty || 0), 0),
        [filtered]
    );

    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const currentPage = Math.min(page, totalPages);
    const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
    const from = filtered.length ? (currentPage - 1) * perPage + 1 : 0;
    const to = Math.min(currentPage * perPage, filtered.length);

    const thClass = "px-4 py-2.5 text-left text-xs font-medium text-muted bg-surface border-b border-line whitespace-nowrap";
    const tdClass = "px-4 py-2.5 border-b border-line text-sm whitespace-nowrap";
    const colCount = isAllWarehouse ? 8 : 7;

    return (
        <div className="max-w-[1600px] mx-auto space-y-5">

            {/* ===== JUDUL + FILTER ===== */}
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-semibold text-body tracking-tight">
                        Lokasi palet
                    </h2>
                    <p className="text-sm text-muted mt-1">
                        Semua stok yang sedang ada di rak, per slot dan palet
                    </p>
                </div>

                <label className="flex flex-col gap-1">
                    <span className="text-xs text-muted">Warehouse</span>
                    <select
                        value={warehouseId}
                        onChange={(e) => setWarehouseId(e.target.value)}
                        className="h-10 min-w-[220px] bg-white border border-line rounded-md px-3 text-sm text-body focus:border-ink focus:ring-0"
                    >
                        <option value="">Semua warehouse</option>
                        {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>
                                {w.name}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            {/* ===== TABEL ===== */}
            <Panel>
                <PanelHeader
                    title="Daftar lokasi"
                    meta={
                        loading
                            ? "Memuat…"
                            : `${formatNumber(filtered.length)} baris stok di rak, total qty ${formatNumber(totalQty)}`
                    }
                >
                    <div className="relative w-full md:w-auto">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                        <input
                            type="text"
                            inputMode="search"
                            placeholder="Cari palet, rak, SKU, item, atau batch"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            className="h-9 w-full md:w-80 pl-9 pr-9 text-sm border border-line rounded-md focus:border-ink focus:ring-0"
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearch("");
                                    setPage(1);
                                }}
                                aria-label="Hapus pencarian"
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-muted hover:text-body hover:bg-surface"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </PanelHeader>

                {error ? (
                    <div className="m-5 bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                ) : (
                    <div className={`overflow-x-auto transition-opacity ${loading ? "opacity-50" : ""}`}>
                        <table className="w-full border-collapse">
                            <thead>
                                <tr>
                                    {isAllWarehouse && <th className={thClass}>Warehouse</th>}
                                    <th className={thClass}>Rak</th>
                                    <th className={thClass}>Level</th>
                                    <th className={thClass}>Palet</th>
                                    <th className={thClass}>SKU</th>
                                    <th className={thClass}>Nama item</th>
                                    <th className={thClass}>Batch</th>
                                    <th className={`${thClass} text-right`}>Qty</th>
                                </tr>
                            </thead>

                            <tbody>
                                {paginated.length > 0 ? (
                                    paginated.map((item, i) => (
                                        <tr key={`${item.rack_code}-${item.level_code}-${item.kode_palet}-${i}`} className="hover:bg-surface/60">
                                            {isAllWarehouse && (
                                                <td className={`${tdClass} text-muted`}>
                                                    {item.warehouse_name || "-"}
                                                </td>
                                            )}
                                            <td className={`${tdClass} font-semibold`}>{item.racks_name || item.rack_code}</td>
                                            <td className={`${tdClass} tabular-nums`}>{item.level_code}</td>
                                            <td className={tdClass}>
                                                {item.kode_palet ? (
                                                    <span className="inline-block px-1.5 py-0.5 rounded bg-ink-100 text-ink text-xs font-semibold">
                                                        {item.kode_palet}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td className={`${tdClass} tabular-nums`}>{item.sku}</td>
                                            <td className={`${tdClass} max-w-[340px] truncate`} title={item.description}>
                                                {item.description}
                                            </td>
                                            <td className={`${tdClass} tabular-nums`}>{item.batch_number}</td>
                                            <td className={`${tdClass} text-right font-semibold tabular-nums`}>
                                                {formatNumber(item.qty)}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={colCount} className="text-center px-4 py-12 text-sm text-muted">
                                            {loading
                                                ? "Memuat lokasi palet…"
                                                : search
                                                    ? `Tidak ada lokasi yang cocok dengan "${search}". Periksa lagi kata kuncinya.`
                                                    : "Belum ada stok di rak untuk warehouse ini."}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ===== PAGINATION ===== */}
                {!error && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-3 text-sm">
                        <div className="flex items-center gap-3 text-muted">
                            <span>
                                {formatNumber(from)}–{formatNumber(to)} dari {formatNumber(filtered.length)}
                            </span>
                            <label className="flex items-center gap-2">
                                <span>Baris per halaman</span>
                                <select
                                    value={perPage}
                                    onChange={(e) => {
                                        setPerPage(Number(e.target.value));
                                        setPage(1);
                                    }}
                                    className="h-8 border border-line rounded-md px-2 text-sm text-body bg-white focus:border-ink focus:ring-0"
                                >
                                    {PER_PAGE_OPTIONS.map((n) => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <div className="flex items-center gap-2">
                            <span className="text-muted mr-1">
                                Halaman {currentPage} dari {totalPages}
                            </span>
                            <button
                                type="button"
                                disabled={currentPage <= 1}
                                onClick={() => setPage(currentPage - 1)}
                                className="h-8 px-3 border border-line rounded-md hover:bg-surface disabled:opacity-40 disabled:hover:bg-transparent"
                            >
                                Sebelumnya
                            </button>
                            <button
                                type="button"
                                disabled={currentPage >= totalPages}
                                onClick={() => setPage(currentPage + 1)}
                                className="h-8 px-3 border border-line rounded-md hover:bg-surface disabled:opacity-40 disabled:hover:bg-transparent"
                            >
                                Berikutnya
                            </button>
                        </div>
                    </div>
                )}
            </Panel>
        </div>
    );
}
